import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import type {
  ZoomOverviewData,
  UsageData,
  PlansData,
  RolesData,
  GroupsData,
  MarketplaceData,
  ArchiveData,
  MeetingQualityData,
  SatisfactionData,
  PhoneCallLogsData,
} from './overview-types';
import { isNotAvailable } from './overview-types';

// ─── Dark theme colors (RGB, matching CSS variables) ────────────────────────
// --background: hsl(230 20% 7%)
const BG: RGB = [14, 15, 21];
// --foreground: hsl(210 40% 98%)
const FG: RGB = [248, 250, 252];
// --card: hsl(230 18% 10%)
const CARD: RGB = [21, 22, 30];
// Slightly lighter than card for alternating rows
const CARD_ALT: RGB = [28, 30, 40];
// --secondary: hsl(230 15% 15%)
const SECONDARY: RGB = [33, 34, 44];
// --primary: hsl(187 85% 53%)  — cyan accent
const PRIMARY: RGB = [33, 213, 237];
// --muted-foreground: hsl(215 20% 65%)
const MUTED: RGB = [148, 163, 184];
// --border: hsl(230 15% 18%)
const BORDER: RGB = [39, 41, 53];
// Status colors
const GREEN: RGB = [34, 197, 94];
const RED: RGB = [239, 68, 68];
const YELLOW: RGB = [234, 179, 8];
// Score category colors (orange shades matching frontend gauges)
const AMBER: RGB = [245, 158, 11];
const ORANGE_400: RGB = [251, 146, 60];
const ORANGE_600: RGB = [234, 88, 12];

type RGB = [number, number, number];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function boolLabel(v?: boolean): string {
  if (v === undefined || v === null) return '—';
  return v ? 'Enabled' : 'Disabled';
}

/** Fill the current page with the dark background color. */
function fillPage(doc: jsPDF) {
  doc.setFillColor(...BG);
  doc.rect(0, 0, 210, 297, 'F');
}

/**
 * Monkey-patch doc.addPage so every new page (whether created manually
 * or by autoTable overflow) automatically gets the dark background drawn
 * BEFORE any content is placed on it.
 */
function patchAddPage(doc: jsPDF) {
  const origAddPage = doc.addPage.bind(doc);
  doc.addPage = function (this: jsPDF, ...args: any[]) {
    origAddPage(...args);
    fillPage(doc);
    return doc;
  } as typeof doc.addPage;
}

function sectionHeader(doc: jsPDF, y: number, title: string): number {
  if (y > 260) {
    doc.addPage(); // background auto-filled via patch
    y = 20;
  }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY);
  doc.text(title, 14, y);
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.3);
  doc.line(14, y + 1.5, 196, y + 1.5);
  return y + 8;
}

/** Shared dark-theme table styles. */
function darkTable(
  doc: jsPDF,
  startY: number,
  head: string[][],
  body: (string | number)[][],
  extra?: Record<string, any>,
): number {
  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    styles: {
      lineColor: [...BORDER],
      lineWidth: 0.15,
      cellPadding: 2.5,
    },
    headStyles: {
      fillColor: [...SECONDARY],
      textColor: [...FG],
      fontSize: 8,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fillColor: [...CARD],
      textColor: [...FG],
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [...CARD_ALT],
    },
    margin: { left: 14, right: 14 },
    // No didDrawPage — page backgrounds handled by the addPage patch
    ...extra,
  });
  return (doc as any).lastAutoTable.finalY;
}

/** Settings-style two-column table with Enabled/Disabled coloring. */
function settingsTable(doc: jsPDF, startY: number, rows: [string, string][]): number {
  return darkTable(doc, startY, [['Setting', 'Status']], rows, {
    tableWidth: 'auto',
    didParseCell(data: any) {
      if (data.section === 'body' && data.column.index === 1) {
        const val = data.cell.raw as string;
        if (val === 'Enabled') data.cell.styles.textColor = [...GREEN];
        else if (val === 'Disabled') data.cell.styles.textColor = [...RED];
      }
    },
  }) + 6;
}

// ─── Chart capture ───────────────────────────────────────────────────────────

interface CapturedImage {
  dataUrl: string;
  width: number;
  height: number;
}

async function captureElement(id: string): Promise<CapturedImage | null> {
  try {
    const el = document.getElementById(id);
    if (!el || el.offsetHeight < 10) return null;

    const canvas = await html2canvas(el, {
      backgroundColor: null,
      scale: 2,
      logging: false,
      useCORS: true,
    });

    // Guard against degenerate captures
    if (canvas.width < 10 || canvas.height < 10) return null;

    return {
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    };
  } catch {
    return null;
  }
}

function embedImage(doc: jsPDF, y: number, img: CapturedImage): number {
  const contentWidth = 182; // mm (A4 width minus margins)
  const imgHeight = (img.height / img.width) * contentWidth;

  if (!isFinite(imgHeight) || imgHeight <= 0) return y;

  if (y + imgHeight > 275) {
    doc.addPage(); // background auto-filled via patch
    y = 20;
  }

  doc.addImage(img.dataUrl, 'PNG', 14, y, contentWidth, imgHeight);
  return y + imgHeight + 6;
}

// ─── Main export ─────────────────────────────────────────────────────────────

export async function generateOverviewPdf(data: ZoomOverviewData): Promise<void> {
  // Capture chart images from the DOM before generating
  const hasUsageData = !isNotAvailable(data.usage) && (data.usage as UsageData).dailyBreakdown?.length > 0;
  const usageChartImg = hasUsageData ? await captureElement('pdf-usage-chart') : null;
  const topMeetingsImg = hasUsageData ? await captureElement('pdf-top-meetings') : null;

  const doc = new jsPDF('p', 'mm', 'a4');
  patchAddPage(doc);
  fillPage(doc);
  let y = 20;

  // ── Title ──────────────────────────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...FG);
  doc.text('Zoom Account Overview', 14, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  const scopeLabel = data.scope === 'admin' ? 'Admin' : 'User-level';
  doc.text(`${data.account.name}  ·  ${data.account.email}  ·  ${scopeLabel}`, 14, y);
  y += 5;

  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, y);
  y += 10;

  // ── Quick Stats ────────────────────────────────────────────────────────────
  y = sectionHeader(doc, y, 'Quick Stats');

  const phoneUsers = data.phone._status === 'provisioned' ? String(data.phone.totalUsers ?? 0) : 'Not provisioned';
  const meetings = isNotAvailable(data.usage) ? 'Requires admin' : String((data.usage as UsageData).totalMeetings);
  const planLabel = isNotAvailable(data.plans)
    ? 'Requires admin'
    : (data.plans as PlansData).planBase?.type || '—';

  y = darkTable(doc, y, [], [
    ['Total Users', String(data.users.totalRecords)],
    ['Active Users', String(data.users.byStatus['active'] ?? data.users.totalRecords)],
    ['Meetings (Month)', meetings],
    ['Cloud Recordings', String(data.recordings.totalRecords)],
    ['Phone Users', phoneUsers],
    ['Plan', planLabel],
  ], {
    showHead: false,
    bodyStyles: {
      fillColor: [...CARD],
      textColor: [...FG],
      fontSize: 9,
    },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [...MUTED] } },
    tableWidth: 'auto',
  }) + 8;

  // ── ZAC Insights ──────────────────────────────────────────────────────────
  if (data.aiSummary && data.aiSummary.overallEvaluation) {
    y = sectionHeader(doc, y, 'ZAC Insights');

    // Score gauge row — draw circular arcs with score numbers
    const scores = data.aiSummary.scores;
    const gauges: { label: string; score: number; color: RGB }[] = [
      { label: 'Security', score: scores.security, color: AMBER },
      { label: 'Adoption', score: scores.adoption, color: ORANGE_400 },
      { label: 'Overall', score: scores.overall, color: scores.overall >= 80 ? GREEN : scores.overall >= 50 ? YELLOW : RED },
      { label: 'Usage', score: scores.usage, color: YELLOW },
      { label: 'Config', score: scores.configuration, color: ORANGE_600 },
    ];

    const gaugeY = y + 12;
    const gaugeSpacing = 34;
    const gaugeStartX = 105 - (gaugeSpacing * 2); // center 5 gauges on page

    for (let i = 0; i < gauges.length; i++) {
      const g = gauges[i]!;
      const cx = gaugeStartX + i * gaugeSpacing;
      const isMain = i === 2; // Overall is the center gauge
      const r = isMain ? 10 : 7;

      // Track circle (dark)
      doc.setDrawColor(...SECONDARY);
      doc.setLineWidth(isMain ? 1.2 : 0.8);
      doc.circle(cx, gaugeY, r, 'S');

      // Score arc
      doc.setDrawColor(...g.color);
      doc.setLineWidth(isMain ? 1.2 : 0.8);
      const startAngle = -90;
      const endAngle = startAngle + (g.score / 100) * 360;
      if (g.score > 0) {
        // Draw arc using small line segments
        const steps = Math.max(1, Math.round(g.score / 2));
        for (let s = 0; s < steps; s++) {
          const a1 = ((startAngle + (s / steps) * (endAngle - startAngle)) * Math.PI) / 180;
          const a2 = ((startAngle + ((s + 1) / steps) * (endAngle - startAngle)) * Math.PI) / 180;
          doc.line(
            cx + r * Math.cos(a1), gaugeY + r * Math.sin(a1),
            cx + r * Math.cos(a2), gaugeY + r * Math.sin(a2),
          );
        }
      }

      // Score number
      doc.setFontSize(isMain ? 11 : 8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...g.color);
      doc.text(String(g.score), cx, gaugeY + (isMain ? 1.5 : 1), { align: 'center' });

      // Label
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      doc.text(g.label, cx, gaugeY + r + 5, { align: 'center' });
    }

    y = gaugeY + 20;

    // Key Points
    if (data.aiSummary.keyPoints.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Key Points', 14, y);
      y += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...FG);
      for (const point of data.aiSummary.keyPoints) {
        if (y > 270) { doc.addPage(); y = 20; }
        const lines = doc.splitTextToSize(`•  ${point}`, 178);
        doc.text(lines, 16, y);
        y += lines.length * 4 + 1.5;
      }
      y += 2;
    }

    // Suggestions
    if (data.aiSummary.suggestions.length > 0) {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Suggestions', 14, y);
      y += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...YELLOW);
      for (const suggestion of data.aiSummary.suggestions) {
        if (y > 270) { doc.addPage(); y = 20; }
        const lines = doc.splitTextToSize(`•  ${typeof suggestion === 'string' ? suggestion : suggestion.text}`, 178);
        doc.text(lines, 16, y);
        y += lines.length * 4 + 1.5;
      }
      y += 2;
    }

    // Overall Evaluation
    if (data.aiSummary.overallEvaluation) {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Overall Evaluation', 14, y);
      y += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      const evalLines = doc.splitTextToSize(data.aiSummary.overallEvaluation, 178);
      if (y + evalLines.length * 4 > 275) { doc.addPage(); y = 20; }
      doc.text(evalLines, 14, y);
      y += evalLines.length * 4 + 4;
    }

    y += 4;
  }

  // ── Usage Chart (captured image) — always start on a new page ─────────────
  if (usageChartImg) {
    doc.addPage();
    y = 20;
    y = sectionHeader(doc, y, 'Daily Activity');
    y = embedImage(doc, y, usageChartImg);
  }

  // ── Top Meetings (captured image or fallback table) ────────────────────────
  if (topMeetingsImg) {
    y = sectionHeader(doc, y, 'Top Meetings');
    y = embedImage(doc, y, topMeetingsImg);
  } else if (!isNotAvailable(data.usage)) {
    const usage = data.usage as UsageData;
    if (usage.topMeetings && usage.topMeetings.length > 0) {
      y = sectionHeader(doc, y, 'Top Meetings');
      y = darkTable(doc, y,
        [['Topic', 'Occurrences', 'Avg Participants', 'Avg Duration', 'Host']],
        usage.topMeetings.map((m) => [
          m.topic,
          String(m.occurrences),
          String(m.avgParticipants),
          formatDuration(m.avgDuration),
          m.host,
        ]),
        {
          columnStyles: {
            0: { cellWidth: 60 },
            1: { halign: 'center', cellWidth: 22 },
            2: { halign: 'center', cellWidth: 28 },
            3: { halign: 'center', cellWidth: 24 },
          },
        },
      ) + 8;
    }
  }

  // ── Security Settings ──────────────────────────────────────────────────────
  y = sectionHeader(doc, y, 'Security Settings');
  const { security } = data.settings;
  const securityRows: [string, string][] = [
    ['Waiting Room', boolLabel(security.waitingRoom)],
    ['Require Meeting Password', boolLabel(security.requirePassword)],
    ['Embed Password in Link', boolLabel(security.embedPasswordInLink)],
    ['End-to-End Encryption', boolLabel(security.e2eEncryption)],
    ['Two-Factor Auth', boolLabel(security.signedLogin)],
  ];
  if (security.approvedDomains && security.approvedDomains.length > 0) {
    securityRows.push(['Approved Domains', security.approvedDomains.join(', ')]);
  }
  y = settingsTable(doc, y, securityRows);

  // ── Meeting & Recording Settings ───────────────────────────────────────────
  y = sectionHeader(doc, y, 'Meeting & Recording Settings');
  const { meeting, recording } = data.settings;
  const meetingRows: [string, string][] = [
    ['Host Video', boolLabel(meeting.hostVideo)],
    ['Participant Video', boolLabel(meeting.participantVideo)],
    ['Join Before Host', boolLabel(meeting.joinBeforeHost)],
    ['Mute Upon Entry', boolLabel(meeting.muteUponEntry)],
    ['Screen Sharing', boolLabel(meeting.screenSharing)],
    ['Breakout Rooms', boolLabel(meeting.breakoutRoom)],
    ['Auto Recording', meeting.autoRecording ?? '—'],
    ['Local Recording', boolLabel(recording.localRecording)],
    ['Cloud Recording', boolLabel(recording.cloudRecording)],
    ['Auto Delete', boolLabel(recording.autoDelete)],
  ];
  if (recording.autoDeleteDays) {
    meetingRows.push(['Delete After', `${recording.autoDeleteDays} days`]);
  }
  y = settingsTable(doc, y, meetingRows);

  // ── Users ──────────────────────────────────────────────────────────────────
  y = sectionHeader(doc, y, 'Users');

  const typeSummary = Object.entries(data.users.byType).map(([t, c]) => `${t}: ${c}`).join('  ·  ');
  const statusSummary = Object.entries(data.users.byStatus).map(([s, c]) => `${s}: ${c}`).join('  ·  ');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  if (typeSummary) { doc.text(typeSummary, 14, y); y += 4; }
  if (statusSummary) { doc.text(statusSummary, 14, y); y += 5; }

  y = darkTable(doc, y,
    [['Name', 'Email', 'Type', 'Role', 'Status', 'Last Login']],
    data.users.users.map((u) => [
      u.name,
      u.email,
      u.typeName,
      u.role || '—',
      u.status || '—',
      u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—',
    ]),
    {
      bodyStyles: { fillColor: [...CARD], textColor: [...FG], fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
      },
    },
  ) + 8;

  // ── Monthly Usage Summary ──────────────────────────────────────────────────
  if (!isNotAvailable(data.usage)) {
    const usage = data.usage as UsageData;
    if (usage.monthlyBreakdown && usage.monthlyBreakdown.length > 0) {
      y = sectionHeader(doc, y, 'Monthly Usage Summary');
      y = darkTable(doc, y,
        [['Month', 'Meetings', 'Participants', 'Minutes']],
        usage.monthlyBreakdown.map((m) => {
          const [yr, mo] = m.month.split('-');
          const label = new Date(Number(yr), Number(mo) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          return [label, m.meetings.toLocaleString(), m.participants.toLocaleString(), m.meetingMinutes.toLocaleString()];
        }),
        {
          columnStyles: {
            1: { halign: 'right' },
            2: { halign: 'right' },
            3: { halign: 'right' },
          },
        },
      ) + 8;
    }
  }

  // ── Phone ──────────────────────────────────────────────────────────────────
  if (data.phone._status === 'provisioned') {
    y = sectionHeader(doc, y, 'Zoom Phone');
    const phoneRows: [string, string][] = [];
    if (data.phone.companyName) phoneRows.push(['Company', data.phone.companyName]);
    phoneRows.push(['Phone Users', String(data.phone.totalUsers ?? 0)]);
    phoneRows.push(['Auto Call Recording', boolLabel(data.phone.autoCallRecording)]);
    phoneRows.push(['E911 Emergency Service', boolLabel(data.phone.emergencyServiceEnabled)]);
    phoneRows.push(['Voicemail', boolLabel(data.phone.voicemailEnabled)]);
    y = settingsTable(doc, y, phoneRows);
  }

  // ── Meeting Quality ──────────────────────────────────────────────────────
  if (data.meetingQuality && !isNotAvailable(data.meetingQuality)) {
    const mq = data.meetingQuality as MeetingQualityData;
    y = sectionHeader(doc, y, 'Meeting Quality');
    const dist = mq.qualityDistribution;
    const total = dist.good + dist.fair + dist.poor + dist.bad;
    y = darkTable(doc, y, [], [
      ['Total Meetings (30d)', String(mq.totalMeetings)],
      ['Good', `${dist.good} (${total > 0 ? Math.round((dist.good / total) * 100) : 0}%)`],
      ['Fair', `${dist.fair} (${total > 0 ? Math.round((dist.fair / total) * 100) : 0}%)`],
      ['Poor', `${dist.poor} (${total > 0 ? Math.round((dist.poor / total) * 100) : 0}%)`],
      ['Bad', `${dist.bad} (${total > 0 ? Math.round((dist.bad / total) * 100) : 0}%)`],
    ], {
      showHead: false,
      bodyStyles: { fillColor: [...CARD], textColor: [...FG], fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [...MUTED] } },
      tableWidth: 'auto',
    }) + 8;
  }

  // ── Client Satisfaction ─────────────────────────────────────────────────
  if (data.satisfaction && !isNotAvailable(data.satisfaction)) {
    const sat = data.satisfaction as SatisfactionData;
    y = sectionHeader(doc, y, 'Client Satisfaction');
    y = darkTable(doc, y, [], [
      ['Satisfaction Rate', `${sat.goodPercent}%`],
      ['Good Responses', String(sat.goodCount)],
      ['Not Good Responses', String(sat.notGoodCount)],
      ['Total Responses', String(sat.totalResponses)],
      ['Period', `${sat.dateRange.from} to ${sat.dateRange.to}`],
    ], {
      showHead: false,
      bodyStyles: { fillColor: [...CARD], textColor: [...FG], fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [...MUTED] } },
      tableWidth: 'auto',
    }) + 8;
  }

  // ── Phone Call Logs ─────────────────────────────────────────────────────
  if (data.phoneCallLogs && !isNotAvailable(data.phoneCallLogs)) {
    const pcl = data.phoneCallLogs as PhoneCallLogsData;
    y = sectionHeader(doc, y, 'Phone Call Summary');
    const avgDur = pcl.avgDurationSeconds < 60
      ? `${pcl.avgDurationSeconds}s`
      : `${Math.floor(pcl.avgDurationSeconds / 60)}m ${pcl.avgDurationSeconds % 60}s`;
    y = darkTable(doc, y, [], [
      ['Total Calls', String(pcl.totalCalls)],
      ['Avg Duration', avgDur],
      ['Inbound', String(pcl.byDirection.inbound)],
      ['Outbound', String(pcl.byDirection.outbound)],
    ], {
      showHead: false,
      bodyStyles: { fillColor: [...CARD], textColor: [...FG], fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [...MUTED] } },
      tableWidth: 'auto',
    }) + 8;
  }

  // ── Rooms ──────────────────────────────────────────────────────────────────
  if (data.rooms._status === 'provisioned' && data.rooms.rooms && data.rooms.rooms.length > 0) {
    y = sectionHeader(doc, y, 'Zoom Rooms');
    y = darkTable(doc, y,
      [['Name', 'Status']],
      data.rooms.rooms.map((r) => [r.name, r.status]),
    ) + 8;
  }

  // ── Integrations ───────────────────────────────────────────────────────────
  const hasRoles = !isNotAvailable(data.roles) && (data.roles as RolesData).roles.length > 0;
  const hasGroups = !isNotAvailable(data.groups) && (data.groups as GroupsData).groups.length > 0;
  const hasApps = !isNotAvailable(data.marketplace) && (data.marketplace as MarketplaceData).totalApps > 0;
  const hasArchive = (data.archive as ArchiveData)._status === 'provisioned';

  if (hasRoles || hasGroups || hasApps || hasArchive) {
    y = sectionHeader(doc, y, 'Integrations & Administration');

    if (hasRoles) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Roles', 14, y);
      y += 4;
      y = darkTable(doc, y,
        [['Role', 'Members']],
        (data.roles as RolesData).roles.map((r) => [r.name, String(r.totalMembers)]),
      ) + 5;
    }

    if (hasGroups) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Groups', 14, y);
      y += 4;
      y = darkTable(doc, y,
        [['Group', 'Members']],
        (data.groups as GroupsData).groups.map((g) => [g.name, String(g.totalMembers)]),
      ) + 5;
    }

    if (hasApps) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...FG);
      doc.text('Marketplace Apps', 14, y);
      y += 4;
      y = darkTable(doc, y,
        [['App', 'Status']],
        (data.marketplace as MarketplaceData).apps.map((a) => [a.name, a.status || '—']),
      ) + 5;
    }
  }

  // ── Footer (on every page) ─────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text('Generated by ZAC — Zoom Admin Copilot', 14, 290);
    doc.text(`Page ${i} of ${pageCount}`, 182, 290);
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`zoom-overview-${dateStr}.pdf`);
}
